import React from 'react';
import {
    filter as _filter,
    find as _find,
    findKey as _findKey,
    get as _get,
    has as _has,
    isEmpty as _isEmpty,
    isNil as _isNil,
} from 'lodash';
import {ApolloConsumer, gql} from '@apollo/client';
import {connect} from 'react-redux';
import {untouch} from 'redux-form';

import DefaultWizard from '@appComponents/DefaultWizard';
import Form from '@appComponents/ReduxFormControls';
import {convertToInt, parseTvStreamingEncoderStreamUrl} from '@utils/helpers';
import mapModulesToProps from '@utils/mapModulesToProps';
import {refetchQueryByName} from '@utils/apollo';
import responseStatus from '@constants/responseStatuses';
import * as VARIABLES from '@constants/variables';
import {
    getMediaconnectOriginEntryPoints,
    getMediaconnecOriginEntryPoint,
} from '@graphql/mediaConnect/query';
import {
    optionsContactPersonsAndOriginStreamName,
    optionsOriginEntryPoints,
    propertyLicenceTechSetupProducts,
    srtOriginEntryPointsForDropdown,
} from '@graphql/events/eventContent';
import {GetRightsScopesOfPropertyLicence} from '@graphql/rightsScope/query';
import {getLink} from '@appComponents/Link';
import {setErrorModal} from '@utils/modal';
import {
    DISTRIBUTION_TYPE_LIVE,
    DISTRIBUTION_TYPE_RECORDING,
    DISTRIBUTION_TYPE_VOD,
    ENCODING_END_MARGIN,
    ENCODING_START_MARGIN,
    EVENT_TYPE_SPORTRADAR,
} from '@constants/variables';
import {CreateBulkDistribution, UpdateBulkDistribution} from '@graphql/distribution/mutation';
import {getDistributionsPartOfProduct, isDistributionPartOfProduct} from '@utils/product';
import {sortDropdownOptionsAlphanumerically} from '@utils/sorters';
import {
    getEventContentVariantDropdownOptions,
    getRightScopesByEventContentVariant, getInitialEventContentVariant,
    renderModalErrorObject,
    setCanDeleteState,
    filterActiveRtmp,
} from '@modules/events/utils/eventContentHelper';
import {CANT_DELETE_CONTENT} from '@modules/events/constants/messages';
import {
    parseOriginEntryPointsOptions,
    prepareSrtOriginEntryPointsOptions,
    showSrtFieldsBasedOnFormValues,
} from '@modules/events/forms/helpers/eventContentForm';

const HAS_VOD_ONLY = 1;
const HAS_VOD = 2;
const HAS_NO_VOD = 3;
const DISTRIBUTION_TYPE_IDS = [
    DISTRIBUTION_TYPE_LIVE,
    DISTRIBUTION_TYPE_RECORDING,
    DISTRIBUTION_TYPE_VOD,
];

export class EventsContentForm extends DefaultWizard {
    constructor(props) {
        super(props);
        this.state = {
            ...this.state,
            event_content_start_datetime: null,
            event_content_end_datetime: null,
            propertyLicenceTechSetupProducts: [],
            mediaConnectOriginEntryPointsEncodingDatacenterId: null,
            queryMediaConnectOriginEntryPointsOfEncodingDatacenterLoading: false,
        };

        props.formParams.products.forEach((product) => {
            this.state[`product_${product.id}_distribution_type_1_always_hidden`] = true;
            this.state[`product_${product.id}_distribution_type_1_start_datetime`] = null;
            this.state[`product_${product.id}_distribution_type_1_end_datetime`] = null;
            this.state[`product_${product.id}_distribution_type_2_always_hidden`] = true;
            this.state[`product_${product.id}_distribution_type_2_start_datetime`] = null;
            this.state[`product_${product.id}_distribution_type_2_end_datetime`] = null;
            this.state[`product_${product.id}_distribution_type_3_always_hidden`] = true;
            this.state[`product_${product.id}_distribution_type_3_start_datetime`] = null;
            this.state[`product_${product.id}_distribution_type_3_end_datetime`] = null;
        });
    }

    componentDidMount() {
        super.componentDidMount();

        if (!this.props.formParams.id && !this.hasAlreadyMainContent(this.props.formParams.eventContents)) {
            this.setField('is_main_content', {checked: true, defaultValue: true});
        }

        if (this.props.formParams.id) {
            this.setField('hide_in_live_control', {hidden: false});
        }

        const contentId = _get(
            this.props,
            'formParams.eventContent.id',
            null
        );

        'undefined' == typeof this.state.canDelete
        && contentId
        && setCanDeleteState(contentId, this.setStateHandler, false);

        const onChangeCallbacks = this.getOnChangeCallbacks();

        this.props.formParams.products.forEach((product) => {
            onChangeCallbacks[`product_${product.id}`] = (data) => {
                this.onChangeProductSwitch(product.id, !data.value);
            };

            DISTRIBUTION_TYPE_IDS.forEach((distributionTypeId) => {
                onChangeCallbacks[`${product.id}_${distributionTypeId}_distribution_usage`] = (data) => {
                    this.onChangeDistributionUsageCheckbox(product.id, distributionTypeId, data.value);
                };
                onChangeCallbacks[`${product.id}_${distributionTypeId}_synchronise_times`] = (data) => {
                    this.changeSynchroniseTimes(data, product.id, distributionTypeId);
                };
                onChangeCallbacks[`${product.id}_${distributionTypeId}_client_selection_type`] = (data) => {
                    this.toggleClientsInputField(data, product.id, distributionTypeId);
                };
            });
        });

        this.setOnChangeCallback(onChangeCallbacks);

        this.setOptionParsers({
            event_content_type: (data) => {
                return this.eventContentTypeParser(data);
            },
        });

        this.setCreateSuccessCallback(this.createSuccessCallback);

        this.setUpdateSuccessCallback(this.updateSuccessCallback);

        this.setDeleteSuccessCallback(this.deleteSuccessCallback);
    }

    getOnChangeCallbacks = () => {
        return {
            event_content_type: (data) => {
                this.onChangeEventContentType(data.value);
            },
            event_content_variant: (data) => {
                this.setState(() => ({
                    processingDistributions: true,
                    selectedEventContentVariant: data.value,
                }), () => {
                    let isProcessingDistributions = false;

                    if (this.state.defaultForm_data?.event_content) {
                        isProcessingDistributions = true;
                    }
                    this.changeAvailableProducts(this.props.formParams, isProcessingDistributions);
                });
            },
            synchronise_times: (data) => {
                if (!data.value) {
                    this.setState(() => ({
                        event_content_start_datetime: this.props.formValues.start_datetime,
                        event_content_end_datetime: this.props.formValues.end_datetime,
                    }));

                    this.setField('start_datetime', {
                        defaultValue: this.props.formParams.event.start_datetime,
                        disabled: !data.value,
                    });
                    this.setField('end_datetime', {
                        defaultValue: this.props.formParams.event.end_datetime,
                        disabled: !data.value,
                    });
                } else {
                    this.setField('start_datetime', {
                        defaultValue: this.state.event_content_start_datetime,
                        disabled: !data.value,
                    });
                    this.setField('end_datetime', {
                        defaultValue: this.state.event_content_end_datetime,
                        disabled: !data.value,
                    });
                }
            },
            is_247_stream: (data) => {
                this.setField('is_247_stream', {checked: !data.checked, defaultValue: !data.checked});
            },
            is_main_content: (data) => {
                this.setField('is_main_content', {checked: !data.checked, defaultValue: !data.checked});
            },
            start_broadcast_automatically: (data) => {
                this.setField('force_broadcast', {hidden: data.value});

                if (data.value) {
                    this.setField('force_broadcast_margin', {hidden: true, defaultValue: null});
                    this.props.change('force_broadcast', false);
                }
            },
            force_broadcast: (data) => {
                this.setField('force_broadcast_margin', {
                    hidden: data.value,
                    defaultValue: data.value ? null : 0,
                });
            },
            set_as_delivered_automatically: (data) => {
                this.setField('force_delivery', {hidden: data.value, defaultValue: false});

                if (data.value) {
                    this.setField('force_delivery_duration', {hidden: true});
                    this.props.change('force_delivery', false);
                    this.props.change('force_delivery_duration', null);
                }
            },
            force_delivery: (data) => {
                this.setField('force_delivery_duration', {hidden: data.value, defaultValue: null});
            },
            hide_in_live_control: (data) => {
                this.setField('hide_in_live_control', {
                    checked: !data.checked,
                    defaultValue: !data.checked,
                });
            },
            // Input Tab
            has_input: (data) => {
                if (!data.value) {
                    this.showHasInputFields();
                    this.showFieldsBasedOnOriginStreamType(this.props.formValues.origin_stream_type);

                    if (convertToInt(this.props.formValues.origin_stream_type) === VARIABLES.ORIGIN_STREAM_TYPE_SRT) {
                        showSrtFieldsBasedOnFormValues(this.props.formValues, this.setField);
                    }

                    this.setField('has_input', {checked: true, defaultValue: true});
                } else {
                    this.hideHasInputFields();

                    this.setField('has_input', {checked: false, defaultValue: false});
                }
            },
            encoding_datacenter: (data) => {
                if (!data.value) {
                    return;
                }

                this.setField('udp_origin_entry_point', {
                    defaultValue: null,
                    disabled: true,
                    loading: true,
                });
                this.setField('sdi_origin_entry_point', {
                    defaultValue: null,
                    disabled: true,
                    loading: true,
                });

                this.onChangeCallbackOriginStreamType(
                    data.value,
                    this.props.formValues.origin_stream_type,
                    this.props.formValues.mediaconnect_origin_entry_point
                );
            },
            production_company: (data) => {
                this.onChangeCallbackProductionCompany(data.value);
            },
            origin_stream_type: (data) => {
                this.showFieldsBasedOnOriginStreamType(data.value);
                this.onChangeCallbackOriginStreamType(
                    this.props.formValues.encoding_datacenter,
                    data.value, this.props.formValues.mediaconnect_origin_entry_point
                );
            },
            rtmp_origin_stream_name: (data) => {
                if (_has(this.state, 'contactPersonsAndOriginStreamNameOfProductionCompany.rtmpOriginStreamNames')) {
                    this.showHideTvStreamingWarningForRtmpOriginStreamName(
                        data.value,
                        this.state.contactPersonsAndOriginStreamNameOfProductionCompany.rtmpOriginStreamNames
                    );
                }
            },
            mediaconnect_origin_entry_point: (data) => {
                this.onChangeCallbackMediaConnectOriginEntryPoint(data.value);
            },
        };
    };

    createSuccessCallback = (response) => {
        let distributions = this.prepareDistributionData(this.state.submitData);

        if (0 < distributions.distributions.length) {
            this.setState(() => ({processingDistributions: true}));

            let dataToSave = {
                ...distributions,
                event_content: String(response.data.createEventContent.id),
                generate_targets: this.getGenerateTargetsCheckboxValue(),
            };

            this
                .mutationContentDistributionCreate(dataToSave)
                .then((mutationResponse) => {
                    this.props.onSuccessCallback();
                    this.showAutomaticTargetGenerationMessage(mutationResponse.data.createBulkDistribution);
                })
                .finally(() => {
                    this.processingDistributionsFinished();
                });
        } else {
            this.props.onSuccessCallback();
            this.props.Modal.setModal({isVisible: false});
            refetchQueryByName('EventQuery');
        }
    };

    updateSuccessCallback = (response) => {
        this.setState(() => ({processingDistributions: true}));

        const dataToSave = {
            ...this.prepareDistributionData(this.state.submitData),
            event_content: String(response.data.updateEventContent.id),
            generate_targets: this.getGenerateTargetsCheckboxValue(),
        };

        this
            .mutationContentDistributionUpdate(dataToSave)
            .then((mutationResponse) => {
                this.props.onSuccessCallback();
                this.showAutomaticTargetGenerationMessage(mutationResponse.data.updateBulkDistribution);
            })
            .finally(() => {
                this.processingDistributionsFinished();
                refetchQueryByName('GetEncodingTargetsForDistributions');
            });
    };

    deleteSuccessCallback = () => {
        refetchQueryByName('EventQuery');
        refetchQueryByName('GetEventContents');
    };

    showOriginStreamTypeRtmpFields = () => {
        this.setField('rtmp_origin_entry_point_application', {hidden: false});
        this.setField('rtmp_origin_stream_name', {hidden: false});
        this.setField('udp_origin_entry_point', {hidden: true});
        this.setField('sdi_origin_entry_point', {hidden: true});
        this.setField('http_origin_entry_point_url', {hidden: true});
        this.setField('mediaconnect_origin_entry_point', {hidden: true});
        this.setField('mediaconnect_origin_feeds', {hidden: true});
        this.setField('srt_origin_entry_point', {hidden: true});
        this.setField('failover_srt_origin_entry_point', {hidden: true});
        this.setField('srt_origin_entry_point_url', {hidden: true});
        this.setField('failover_srt_origin_entry_point_url', {hidden: true});
        this.setField('is_srt_origin_entry_point_in_failover', {hidden: true});
        this.setField('override_ad_setup', {hidden: true});
        this.setField('srt_has_scte35', {hidden: true});
        this.setField('srt_ad_splice_config', {hidden: true});
    };

    showOriginStreamTypeUdpFields = () => {
        this.setField('rtmp_origin_entry_point_application', {hidden: true});
        this.setField('rtmp_origin_stream_name', {hidden: true});
        this.setField('udp_origin_entry_point', {hidden: false});
        this.setField('sdi_origin_entry_point', {hidden: true});
        this.setField('http_origin_entry_point_url', {hidden: true});
        this.setField('mediaconnect_origin_entry_point', {hidden: true});
        this.setField('mediaconnect_origin_feeds', {hidden: true});
        this.setField('srt_origin_entry_point', {hidden: true});
        this.setField('failover_srt_origin_entry_point', {hidden: true});
        this.setField('srt_origin_entry_point_url', {hidden: true});
        this.setField('failover_srt_origin_entry_point_url', {hidden: true});
        this.setField('is_srt_origin_entry_point_in_failover', {hidden: true});
        this.setField('override_ad_setup', {hidden: true});
        this.setField('srt_has_scte35', {hidden: true});
        this.setField('srt_ad_splice_config', {hidden: true});
    };

    showOriginStreamTypeSdiFields = () => {
        this.setField('rtmp_origin_entry_point_application', {hidden: true});
        this.setField('rtmp_origin_stream_name', {hidden: true});
        this.setField('udp_origin_entry_point', {hidden: true});
        this.setField('sdi_origin_entry_point', {hidden: false});
        this.setField('http_origin_entry_point_url', {hidden: true});
        this.setField('mediaconnect_origin_entry_point', {hidden: true});
        this.setField('mediaconnect_origin_feeds', {hidden: true});
        this.setField('srt_origin_entry_point', {hidden: true});
        this.setField('failover_srt_origin_entry_point', {hidden: true});
        this.setField('srt_origin_entry_point_url', {hidden: true});
        this.setField('failover_srt_origin_entry_point_url', {hidden: true});
        this.setField('is_srt_origin_entry_point_in_failover', {hidden: true});
        this.setField('override_ad_setup', {hidden: true});
        this.setField('srt_has_scte35', {hidden: true});
        this.setField('srt_ad_splice_config', {hidden: true});
    };

    showOriginStreamTypeHttpFields = () => {
        this.setField('rtmp_origin_entry_point_application', {hidden: true});
        this.setField('rtmp_origin_stream_name', {hidden: true});
        this.setField('udp_origin_entry_point', {hidden: true});
        this.setField('sdi_origin_entry_point', {hidden: true});
        this.setField('http_origin_entry_point_url', {hidden: false});
        this.setField('mediaconnect_origin_entry_point', {hidden: true});
        this.setField('mediaconnect_origin_feeds', {hidden: true});
        this.setField('srt_origin_entry_point', {hidden: true});
        this.setField('failover_srt_origin_entry_point', {hidden: true});
        this.setField('srt_origin_entry_point_url', {hidden: true});
        this.setField('failover_srt_origin_entry_point_url', {hidden: true});
        this.setField('is_srt_origin_entry_point_in_failover', {hidden: true});
        this.setField('override_ad_setup', {hidden: true});
        this.setField('srt_has_scte35', {hidden: true});
        this.setField('srt_ad_splice_config', {hidden: true});
    };

    showHasInputFields = () => {
        this.setField('contribution_type', {hidden: false});
        this.setField('encoding_datacenter', {hidden: false});
        this.setField('encoding_redundancy_type', {hidden: false});
        this.setField('production_company', {hidden: false});
        this.setField('contact_person', {hidden: false});
        this.setField('origin_stream_type', {hidden: false});
        this.setField('sequence', {hidden: false});
    };

    showOriginStreamTypeMediaConnectFields = () => {
        this.setField('rtmp_origin_entry_point_application', {hidden: true});
        this.setField('rtmp_origin_stream_name', {hidden: true});
        this.setField('udp_origin_entry_point', {hidden: true});
        this.setField('sdi_origin_entry_point', {hidden: true});
        this.setField('http_origin_entry_point_url', {hidden: true});
        this.setField('mediaconnect_origin_entry_point', {hidden: false});
        this.setField('srt_origin_entry_point', {hidden: true});
        this.setField('failover_srt_origin_entry_point', {hidden: true});
        this.setField('srt_origin_entry_point_url', {hidden: true});
        this.setField('failover_srt_origin_entry_point_url', {hidden: true});
        this.setField('is_srt_origin_entry_point_in_failover', {hidden: true});
        this.setField('override_ad_setup', {hidden: true});
        this.setField('srt_has_scte35', {hidden: true});
        this.setField('srt_ad_splice_config', {hidden: true});
    };

    showOriginStreamTypeSrtFields = () => {
        this.setField('rtmp_origin_entry_point_application', {hidden: true});
        this.setField('rtmp_origin_stream_name', {hidden: true});
        this.setField('udp_origin_entry_point', {hidden: true});
        this.setField('sdi_origin_entry_point', {hidden: true});
        this.setField('http_origin_entry_point_url', {hidden: true});
        this.setField('mediaconnect_origin_entry_point', {hidden: true});
        this.setField('mediaconnect_origin_feeds', {hidden: true});
        this.setField('srt_origin_entry_point', {hidden: false});
        this.setField('failover_srt_origin_entry_point', {hidden: true});
        this.setField('srt_origin_entry_point_url', {hidden: true});
        this.setField('failover_srt_origin_entry_point_url', {hidden: true});
        this.setField('is_srt_origin_entry_point_in_failover', {hidden: true});
        this.setField('override_ad_setup', {hidden: true});
        this.setField('srt_has_scte35', {hidden: true});
        this.setField('srt_ad_splice_config', {hidden: true});
    };

    hideHasInputFields = () => {
        this.setField([
            'contribution_type',
            'encoding_datacenter',
            'encoding_redundancy_type',
            'production_company',
            'contact_person',
            'origin_stream_type',
            'rtmp_origin_entry_point_application',
            'rtmp_origin_stream_name',
            'udp_origin_entry_point',
            'sdi_origin_entry_point',
            'http_origin_entry_point_url',
            'mediaconnect_origin_entry_point',
            'mediaconnect_origin_feeds',
            'srt_origin_entry_point',
            'failover_srt_origin_entry_point',
            'srt_origin_entry_point_url',
            'failover_srt_origin_entry_point_url',
            'is_srt_origin_entry_point_in_failover',
            'override_ad_setup',
            'srt_has_scte35',
            'srt_ad_splice_config',
            'sequence',
        ], {hidden: true});
    };

    showFieldsBasedOnOriginStreamType = (originStreamType) => {
        switch (convertToInt(originStreamType)) {
            case VARIABLES.ORIGIN_STREAM_TYPE_RTMP:
                this.showOriginStreamTypeRtmpFields();
                break;
            case VARIABLES.ORIGIN_STREAM_TYPE_UDP:
                this.showOriginStreamTypeUdpFields();
                break;
            case VARIABLES.ORIGIN_STREAM_TYPE_SDI:
                this.showOriginStreamTypeSdiFields();
                break;
            case VARIABLES.ORIGIN_STREAM_TYPE_HTTP:
                this.showOriginStreamTypeHttpFields();
                break;
            case VARIABLES.ORIGIN_STREAM_TYPE_MEDIACONNECT:
                this.showOriginStreamTypeMediaConnectFields();
                break;
            case VARIABLES.ORIGIN_STREAM_TYPE_SRT:
                this.showOriginStreamTypeSrtFields();
                break;
        }
    };

    showAvailableProducts = (availableProducts) => {
        availableProducts.map((availableProduct) => {
            this.setField(`product_${availableProduct}`, {
                hidden: false,
            });
            this.setField(`product_${availableProduct}_divider`, {hidden: false});
        });
    };

    setMediaConnectOfEncodingDataCenterInput = (encodingDatacenter, mediaConnectOriginEntryPointId) => {
        this.setField('mediaconnect_origin_entry_point', {disabled: true, loading: true});
        this.runApolloRequest('query', {
            query: getMediaconnectOriginEntryPoints,
            variables: {
                encodingDatacenter: [convertToInt(encodingDatacenter)],
            },
            fetchPolicy: 'network-only',
        }).then((response) => {
            this.setState(() => ({
                mediaConnectOriginEntryPointsEncodingDatacenterId: convertToInt(encodingDatacenter),
                queryMediaConnectOriginEntryPointsOfEncodingDatacenterLoading: true,
            }));

            if (_isEmpty(response.data.mediaconnectOriginEntryPoints)) {
                this.setField('mediaconnect_origin_entry_point', {
                    disabled: false,
                    loading: false,
                    options: null,
                    defaultValue: null,
                    value: null,
                });
                this.setField('mediaconnect_origin_feeds', {
                    hidden: true,
                    defaultValue: null,
                    value: null,
                });
            } else {
                const isMediaConnectContainsOptions = (mediaconnect) =>
                        mediaconnect.id === this.props.formValues.mediaconnect_origin_entry_point,
                    isMediaConnectIdContainsInResponse = response
                        .data.mediaconnectOriginEntryPoints.some(isMediaConnectContainsOptions);

                if (isMediaConnectIdContainsInResponse) {
                    this.setField('mediaconnect_origin_entry_point', {
                        disabled: false,
                        loading: false,
                        options: response.data.mediaconnectOriginEntryPoints,
                    });
                    this.setMediaConnectOriginFeeds(mediaConnectOriginEntryPointId);
                } else {
                    this.setField('mediaconnect_origin_entry_point', {
                        disabled: false,
                        loading: false,
                        value: null,
                        defaultValue: null,
                        options: response.data.mediaconnectOriginEntryPoints,
                    });
                    this.setField('mediaconnect_origin_feeds', {
                        hidden: true,
                        value: null,
                    });
                }
            }
        }).finally(() => {
            this.setState(() => ({
                queryMediaConnectOriginEntryPointsOfEncodingDatacenterLoading: false,
            }));
        });
    };

    /** Helping methods for querying additional data from graphql */
    queryUdpAndSdiOriginEntryPointsOfEncodingDatacenter = (encodingDatacenter) => {
        this.setState(() => (
            {queryUdpAndSdiOriginEntryPointsOfEncodingDatacenterLoading: true})
        );

        return this.runApolloRequest('query', {
            query: gql`${optionsOriginEntryPoints}`,
            variables: {
                encodingDatacentersForUpd: [convertToInt(encodingDatacenter)],
                encodingDatacentersForSdi: [encodingDatacenter],
            },
        });
    };

    querySrtOriginEntryPointsOptions = () => {
        return this.runApolloRequest('query', {
            query: gql`${srtOriginEntryPointsForDropdown}`,
        });
    };

    queryContactPersonsAndOriginStreamNameOfProductionCompany = (productionCompany) => {
        this.setState(() => (
            {queryContactPersonsAndOriginStreamNameOfProductionCompanyLoading: true})
        );

        return this.runApolloRequest('query', {
            fetchPolicy: 'network-only',
            query: gql`${optionsContactPersonsAndOriginStreamName}`,
            variables: {
                productionCompany: convertToInt(productionCompany),
            },
        });
    };

    queryRightScopesOfPropertyLicence = (propertyLicence, event) => {
        this.setState(() => ({queryRightScopesOfPropertyLicenceLoaded: false}));

        return this.runApolloRequest('query', {
            fetchPolicy: 'network-only',
            query: GetRightsScopesOfPropertyLicence,
            variables: {
                propertyLicence: convertToInt(propertyLicence),
                event: convertToInt(event),
            },
        });
    };

    queryPropertyLicenceTechSetupProducts = (propertyLicenceTechSetup) => {
        this.setState(() => ({queryPropertyLicenceTechSetupProductsLoading: true}));

        return this.runApolloRequest('query', {
            query: gql`${propertyLicenceTechSetupProducts}`,
            variables: {
                propertyLicenceTechSetup: convertToInt(propertyLicenceTechSetup),
            },
        });
    };

    queryMediaConnectOriginEntryPointsFeed = (mediaConnectOriginEntryPointId) => {

        return this.runApolloRequest('query', {
            query: getMediaconnecOriginEntryPoint,
            variables: {
                id: convertToInt(mediaConnectOriginEntryPointId),
            },
        });
    };

    mutationContentDistributionCreate = (data) => (
        this.runApolloRequest('mutate', {
            mutation: CreateBulkDistribution,
            variables: data,
        })
    );

    mutationContentDistributionUpdate = (data) => (
        this.runApolloRequest('mutate', {
            mutation: UpdateBulkDistribution,
            variables: data,
        })
    );

    selectProductsBasedOnPropertyLicenceTechSetup = (propertyLicenceTechSetups) => {
        this
            .queryPropertyLicenceTechSetupProducts(propertyLicenceTechSetups[0].id)
            .then((result) => {
                this.setInputAndDistributionFields(
                    result.data.propertyLicenceTechSetupProducts,
                    this.state.currentSelectedEventContentType
                );
                this.setState(() => ({
                    propertyLicenceTechSetupProducts: result.data.propertyLicenceTechSetupProducts,
                }));
            })
            .finally(() => {
                this.setState(() => ({
                    queryPropertyLicenceTechSetupProductsLoading: false,
                }));
            });
    };

    setInputAndDistributionFields = (propertyLicenceTechSetupProducts, currentSelectedEventContentType) => {
        const selectedContentType = _find(this.state.eventContentTypes, {
            key: currentSelectedEventContentType,
        });

        this.props.GraphQLOptionsData.productDistributionTypes.forEach((distributionType) => {
            if (this.state.availableProducts.includes(distributionType.product.id)) {
                DISTRIBUTION_TYPE_IDS.forEach((distribution) => {
                    const shouldBeShown = this.shouldDistributionBeShown(
                        distributionType.product.id,
                        distribution,
                        selectedContentType
                    );

                    this.toggleDistributionGroup(
                        distributionType.product.id,
                        distribution,
                        this.props.formParams.id ? shouldBeShown : false
                    );
                });
            }
        });

        if (0 < this.state.propertyLicenceTechSetups.length && !this.props.formParams.id) {
            let setup = this.state.propertyLicenceTechSetups[0],
                encodingDatacenterId = _get(setup, 'encoding_datacenter.id', null),
                encodingRedundancyTypeId = _get(setup, 'encoding_redundancy_type.id', null);

            this.setField('contribution_type', {defaultValue: _get(setup, 'contribution_type.id', null)});
            this.setField('encoding_datacenter', {defaultValue: encodingDatacenterId});
            this.setField('encoding_redundancy_type', {defaultValue: encodingRedundancyTypeId});
            this.setField('origin_stream_type', {defaultValue: _get(setup, 'origin_stream_type.id', null)});

            this.showFieldsBasedOnOriginStreamType(_get(setup, 'origin_stream_type.id', null));

            if (encodingDatacenterId) {
                this.loadUdpAndSdi(
                    encodingDatacenterId,
                    this.getTournamentOfEvent(),
                    this.getContentCategoryOfEvent()
                );
            }
        }
    };

    isTvStreaming = () => (
        this.props.formValues.is_main_content
        && VARIABLES.ORIGIN_STREAM_TYPE_RTMP === convertToInt(
            _get(this.state.propertyLicenceTechSetups, '[0].origin_stream_type.id', null)
        ) && _get(this.state.propertyLicenceTechSetups, '[0].is_tv_streaming', false)
        && _get(this.props.formParams, 'event.sr_event.match.tv_streaming_encoder.id', false)
    );

    shouldDistributionBeShown = (product, distribution, selectedContentType, productStatus = null) => {
        const distributionsPartOfProducts = getDistributionsPartOfProduct(
            product, distribution, this.props.GraphQLOptionsData.productDistributionTypes);

        if (0 < distributionsPartOfProducts.length) {
            if (
                0 < this.isDistributionPartOfEventContentType(selectedContentType, distribution).length
                && this.hasDistributionMediaRights(product, distribution)
            ) {
                this.setDistributionGroupValues(product, distribution, productStatus);

                if (!_get(distributionsPartOfProducts, '0.is_default', false)) {
                    this.setField(`${product}_${distribution}_distribution_usage`, {defaultValue: false});
                }

                this.toggleDistributionGroup(product, distribution, true);

                this.setState(() => ({
                    [`product_${product}_distribution_type_${distribution}_always_hidden`]: false,
                }));

                return true;
            }
        }

        this.setState(() => ({
            [`product_${product}_distribution_type_${distribution}_always_hidden`]: true,
        }));

        this.toggleDistributionGroup(product, distribution, false);

        return false;
    };

    hasDistributionMediaRights = (product, distribution) => {
        const distributionInt = convertToInt(distribution);

        return 0 < this.state.filteredRightsScopes.filter((rightsScope) => (
            rightsScope.usages.find((usage) => {
                const wayOfTransmissionIdInt = convertToInt(usage.way_of_transmission.id);

                return convertToInt(product) === convertToInt(usage.product.id)
                    && (
                        (
                            VARIABLES.DISTRIBUTION_TYPE_LIVE === distributionInt
                            && (
                                VARIABLES.WAY_OF_TRANSMISSION_LIVE === wayOfTransmissionIdInt
                                || VARIABLES.WAY_OF_TRANSMISSION_DELAYED === wayOfTransmissionIdInt
                            )
                        )
                        || VARIABLES.DISTRIBUTION_TYPE_RECORDING === distributionInt
                        || (
                            VARIABLES.DISTRIBUTION_TYPE_VOD === distributionInt
                            && VARIABLES.WAY_OF_TRANSMISSION_ON_DEMAND === wayOfTransmissionIdInt
                        )
                    );
            }) !== undefined
        )).length;
    };

    checkIfDistributionShouldBeShown = (product, distribution) => {
        const selectedContentType = _find(this.state.eventContentTypes, {
                key: this.state.currentSelectedEventContentType,
            }),
            isDistributionPartOfProducts = isDistributionPartOfProduct(
                product, distribution, this.props.GraphQLOptionsData.productDistributionTypes);

        return isDistributionPartOfProducts
            && 0 < this.isDistributionPartOfEventContentType(selectedContentType, distribution).length
            && this.hasDistributionMediaRights(product, distribution);
    };

    isDistributionPartOfEventContentType = (eventContentType, distribution) => {
        return _filter(_get(eventContentType, 'distribution_types', []), (o) => (
            convertToInt(o.id) === convertToInt(distribution)
        ));
    };

    setDistributionGroupValues = (product, distribution, productStatus) => {
        this.setField(`${product}_${distribution}_distribution_usage`, {defaultValue: true});

        if (productStatus) {
            this.setField(`${product}_${distribution}_product_status`, {defaultValue: productStatus});
        }

        this.setField(`${product}_${distribution}_start_datetime`, {
            defaultValue: this.props.formParams.event.start_datetime,
            disabled: true,
        });
        this.setField(`${product}_${distribution}_end_datetime`, {
            defaultValue: this.props.formParams.event.end_datetime,
            disabled: true,
        });
    };

    toggleDistributionGroup = (product, distribution, toggle) => {
        this.setField(`${product}_${distribution}_distribution_usage`, {hidden: !toggle});

        if (this.props.formValues[`${product}_${distribution}_distribution_usage`]) {
            this.setField(`${product}_${distribution}_product_status`, {hidden: !toggle});
            this.setField(`${product}_${distribution}_synchronise_times`, {hidden: !toggle});
            this.setField(`${product}_${distribution}_start_datetime`, {hidden: !toggle});
            this.setField(`${product}_${distribution}_end_datetime`, {hidden: !toggle});
            this.setField(`${product}_${distribution}_name`, {hidden: !toggle});
            this.setField(`${product}_${distribution}_client_selection_type`, {hidden: !toggle});
            this.setField(`${product}_${distribution}_clients`, {
                hidden: (
                    !toggle
                    || VARIABLES.CLIENT_SELECTION_TYPE_ALL === convertToInt(
                        this.props.formValues[`${product}_${distribution}_client_selection_type`]
                    )
                ),
            });

            if (VARIABLES.DISTRIBUTION_TYPE_RECORDING !== distribution) {
                this.setField(`${product}_${distribution}_broadcast_status`, {hidden: !toggle});
            }
        }
    };

    changeSynchroniseTimes = (data, product, distribution) => {
        this.setField(`${product}_${distribution}_start_datetime`, {disabled: !data.value});
        this.setField(`${product}_${distribution}_end_datetime`, {disabled: !data.value});

        if (data.value) {
            this.setField(`${product}_${distribution}_start_datetime`, {
                defaultValue: this.state[`product_${product}_distribution_type_${distribution}_start_datetime`],
            });
            this.setField(`${product}_${distribution}_end_datetime`, {
                defaultValue: this.state[`product_${product}_distribution_type_${distribution}_end_datetime`],
            });
        } else {
            this.setState(() => ({
                [`product_${product}_distribution_type_${distribution}_start_datetime`]:
                    this.props.formValues[`${product}_${distribution}_start_datetime`],
                [`product_${product}_distribution_type_${distribution}_end_datetime`]:
                    this.props.formValues[`${product}_${distribution}_end_datetime`],
            }));

            this.setField(`${product}_${distribution}_start_datetime`, {
                defaultValue: this.props.formValues.start_datetime,
            });
            this.setField(`${product}_${distribution}_end_datetime`, {
                defaultValue: this.props.formValues.end_datetime,
            });
        }
    };

    toggleClientsInputField = (data, product, distribution) => {
        this.setField(`${product}_${distribution}_clients`, {
            hidden: VARIABLES.CLIENT_SELECTION_TYPE_ALL === convertToInt(data.value),
        });
    };

    selectProductsBasedOnEventContentDistributionData = (distributions) => {
        this.props.formParams.products.map((product) => {
            DISTRIBUTION_TYPE_IDS.forEach((distributionId) => {
                const distributionSet = distributions.find((d) => (
                    convertToInt(d.distribution_type.id) === convertToInt(distributionId)
                    && convertToInt(d.product.id) === convertToInt(product.id)
                ));

                if (!distributionSet) {
                    this.setField(`${product.id}_${distributionId}_start_datetime`, {
                        defaultValue: this.props.formValues.start_datetime,
                        disabled: true,
                    });
                    this.setField(`${product.id}_${distributionId}_end_datetime`, {
                        defaultValue: this.props.formValues.end_datetime,
                        disabled: true,
                    });
                } else if (distributionSet) {
                    DISTRIBUTION_TYPE_IDS.filter((distributionForFilter) => (
                        !distributions.find((d) => (
                            convertToInt(d.distribution_type.id) === convertToInt(distributionForFilter)
                            && convertToInt(d.product.id) === convertToInt(product.id)
                        ))
                    )).forEach((distribution) => {
                        this.setField(`${product.id}_${distribution}_distribution_usage`, {
                            defaultValue: false,
                            hidden: false,
                        });
                    });
                }

                this.setState(() => ({
                    [`product_${product.id}_distribution_type_${distributionId}_start_datetime`]:
                    this.props.formValues.start_datetime,
                    [`product_${product.id}_distribution_type_${distributionId}_end_datetime`]:
                    this.props.formValues.end_datetime,
                    [`product_${product.id}_distribution_type_${distributionId}_always_hidden`]:
                        !this.checkIfDistributionShouldBeShown(product.id, distributionId),
                }));

            });
        });

        distributions.map((distribution) => {
            const product = distribution.product.id,
                distributionType = distribution.distribution_type.id;

            this.setField(`product_${product}`, {checked: true, defaultValue: true});
            this.setField(`${product}_${distributionType}_distribution_usage`, {
                defaultValue: true,
                hidden: false,
            });
            this.setField(`${product}_${distributionType}_product_status`, {
                defaultValue: distribution.product_status.id,
                hidden: false,
            });
            this.setField(`${product}_${distributionType}_name`, {
                defaultValue: distribution.name,
                hidden: false,
            });
            this.setField(`${product}_${distributionType}_client_selection_type`, {
                defaultValue: distribution.client_selection_type.id,
                hidden: false,
            });
            this.setField(`${product}_${distributionType}_clients`, {
                defaultValue: distribution.clients.map((client) => client.id),
                hidden: VARIABLES.CLIENT_SELECTION_TYPE_ALL === convertToInt(distribution.client_selection_type.id),
            });
            this.setField(`${product}_${distributionType}_synchronise_times`, {
                defaultValue: distribution.synchronise_times,
                hidden: false,
            });
            this.setField(`${product}_${distributionType}_start_datetime`, {
                defaultValue: distribution.start_datetime || this.props.formValues.start_datetime,
                disabled: distribution.synchronise_times,
                hidden: false,
                timestamp: Date.now(),
            });
            this.setField(`${product}_${distributionType}_end_datetime`, {
                defaultValue: distribution.end_datetime || this.props.formValues.end_datetime,
                disabled: distribution.synchronise_times,
                hidden: false,
                timestamp: Date.now(),
            });

            if (VARIABLES.DISTRIBUTION_TYPE_RECORDING !== convertToInt(distributionType)) {
                this.setField(`${product}_${distributionType}_broadcast_status`, {
                    defaultValue: distribution.broadcast_status ? distribution.broadcast_status.id.toString() : null,
                    hidden: false,
                });
            }
        });
    };

    additionalChangeTabsCallback = (currentStep) => {
        if (1 === currentStep) {
            if (this.props.formParams.id) {
                if (this.isTvStreaming()) {
                    this.showHideTvStreamingWarningForRtmpOriginEntryPointApplication(
                        this.props.formValues.rtmp_origin_entry_point_application
                    );
                    this.showHideTvStreamingWarningForRtmpOriginStreamName(
                        this.props.formValues.rtmp_origin_stream_name,
                        this.state.contactPersonsAndOriginStreamNameOfProductionCompany.rtmpOriginStreamNames
                    );
                } else {
                    this.setField('rtmp_origin_entry_point_application', {customWarning: null});
                    this.setField('rtmp_origin_stream_name', {customWarning: null});
                }
            }
        } else if (2 === currentStep) {
            this.changeDistributionDatetimesForAddMode();
        }
    };

    stepChangedCallback = (currentStep) => {
        const EVENT_CONTENT = 'event content';

        if (1 === currentStep) {
            if (VARIABLES.ORIGIN_STREAM_TYPE_MEDIACONNECT === convertToInt(this.props.formValues.origin_stream_type)) {
                this.setMediaConnectOfEncodingDataCenterInput(
                    this.props.formValues.encoding_datacenter,
                    this.props.formValues.mediaconnect_origin_entry_point
                );
            }

            if (!this.props.formParams.id) {
                this.showHasInputFields();

                if (this.isTvStreaming()) {
                    const productionCompany = `${VARIABLES.TV_STREAMING_PRODUCTION_COMPANY}`;

                    this.setField('production_company', {defaultValue: productionCompany});
                    this.onChangeCallbackProductionCompany(productionCompany);
                }

                this.setField('has_input', {checked: true, defaultValue: true});
            }

            this.props.dispatch(untouch(EVENT_CONTENT, 'contribution_type'));
            this.props.dispatch(untouch(EVENT_CONTENT, 'encoding_datacenter'));
            this.props.dispatch(untouch(EVENT_CONTENT, 'encoding_redundancy_type'));
            this.props.dispatch(untouch(EVENT_CONTENT, 'production_company'));
            this.props.dispatch(untouch(EVENT_CONTENT, 'contact_person'));
            this.props.dispatch(untouch(EVENT_CONTENT, 'origin_stream_type'));
            this.props.dispatch(untouch(EVENT_CONTENT, 'rtmp_origin_entry_point_application'));
            this.props.dispatch(untouch(EVENT_CONTENT, 'rtmp_origin_stream_name'));
            this.props.dispatch(untouch(EVENT_CONTENT, 'udp_origin_entry_point'));
            this.props.dispatch(untouch(EVENT_CONTENT, 'sdi_origin_entry_point'));
            this.props.dispatch(untouch(EVENT_CONTENT, 'http_origin_entry_point_url'));
            this.props.dispatch(untouch(EVENT_CONTENT, 'srt_origin_entry_point'));
            this.props.dispatch(untouch(EVENT_CONTENT, 'srt_ad_splice_config'));
        } else if (2 === currentStep) {
            this.props.formParams.products.forEach((product) => {
                this.props.dispatch(untouch(EVENT_CONTENT, `product_${product.id}`));

                DISTRIBUTION_TYPE_IDS.forEach((distribution) => {
                    this.props.dispatch(untouch(EVENT_CONTENT, `${product.id}_${distribution}_distribution_usage`));
                    this.props.dispatch(untouch(EVENT_CONTENT, `${product.id}_${distribution}_product_status`));
                    this.props.dispatch(untouch(EVENT_CONTENT, `${product.id}_${distribution}_start_datetime`));
                    this.props.dispatch(untouch(EVENT_CONTENT, `${product.id}_${distribution}_end_datetime`));
                    this.props.dispatch(untouch(EVENT_CONTENT, `${product.id}_${distribution}_name`));
                    this.props.dispatch(untouch(EVENT_CONTENT, `${product.id}_${distribution}_client_selection_type`));
                    this.props.dispatch(untouch(EVENT_CONTENT, `${product.id}_${distribution}_clients`));
                });
            });

            if (!this.props.formParams.id) {
                this.changeDistributionDatetimesForAddMode();

                if (this.state.propertyLicenceTechSetupProducts) {
                    this.toggleDistributionsForPropertyLicenceTechSetup();
                }
            }
        }
    };

    toggleDistributionsForPropertyLicenceTechSetup = () => {
        this.state.propertyLicenceTechSetupProducts.map((item) => {
            if (this.state.availableProducts.includes(item.product.id)) {
                let distributions = Object.keys(this.props.formValues).filter(key =>
                    key.startsWith(`${item.product.id}`)
                );

                if (0 < distributions.length) {
                    this.setField(
                        `product_${item.product.id}`,
                        {checked: true, defaultValue: true},
                        this.toggleGenerateTargets
                    );

                    DISTRIBUTION_TYPE_IDS.forEach((distribution) => {
                        this.setField(`${item.product.id}_${distribution}_product_status`, {
                            defaultValue: item.product_status.id,
                        });

                        this.toggleDistributionGroup(item.product.id, distribution, true);
                    });
                }
            }
        });
    };

    changeDistributionDatetimesForAddMode = () => {
        let newState = {};

        this.props.formParams.products.forEach((product) => {
            DISTRIBUTION_TYPE_IDS.forEach((distribution) => {
                if (this.props.formValues[`${product.id}_${distribution}_synchronise_times`]) {
                    this.setField(`${product.id}_${distribution}_start_datetime`, {
                        defaultValue: this.props.formValues.start_datetime,
                        timestamp: Date.now(),
                    });
                    this.setField(`${product.id}_${distribution}_end_datetime`, {
                        defaultValue: this.props.formValues.end_datetime,
                        timestamp: Date.now(),
                    });

                    newState = Object.assign(newState, {
                        [`product_${product.id}_distribution_type_${distribution}_start_datetime`]:
                        this.props.formValues.start_datetime,
                        [`product_${product.id}_distribution_type_${distribution}_end_datetime`]:
                        this.props.formValues.end_datetime,
                    });
                }
            });
        });

        this.setState(() => (newState));
    };

    loadUdpAndSdi = (encoding_datacenter, tournaments, content_categories) => {
        this.setField('udp_origin_entry_point', {disabled: true, loading: true});
        this.setField('sdi_origin_entry_point', {disabled: true, loading: true});

        this
            .queryUdpAndSdiOriginEntryPointsOfEncodingDatacenter(encoding_datacenter)
            .then((data) => {
                this.setField('udp_origin_entry_point', {
                    disabled: false,
                    loading: false,
                    options: parseOriginEntryPointsOptions(
                        data.data.udpOriginEntryPoints,
                        tournaments,
                        content_categories
                    ),
                });
                this.setField('sdi_origin_entry_point', {
                    disabled: false,
                    loading: false,
                    options: data.data.sdiOriginEntryPoints,
                });
            })
            .finally(() => {
                this.setState(() => ({queryUdpAndSdiOriginEntryPointsOfEncodingDatacenterLoading: false}));
            });

    };

    getTournamentOfEvent = () => {
        const event = this.props.formParams.event;

        if (_get(event, 'sr_event.match.tournament', null)) {
            return event.sr_event.match.tournament.id;
        } else {
            return _get(event, 'sr_event.tournament.id', null);
        }
    };

    getContentCategoryOfEvent = () => {
        const event = this.props.formParams.event;

        return event.av_event ? event.av_event.content_category.id : null;
    };

    getTvStreamingEncoderStreamUrl = () => (
        _get(this.props.formParams, 'event.sr_event.match.tv_streaming_encoder.stream_url', false)
    );

    showHideTvStreamingWarningForRtmpOriginEntryPointApplication = (value) => {
        if (
            this.props.formParams.id
            && value
            && this.isTvStreaming()
        ) {
            const streamUrl = this.getTvStreamingEncoderStreamUrl(),
                parsedUrl = parseTvStreamingEncoderStreamUrl(streamUrl);

            const {
                GraphQLOptionsData: {rtmpOriginEntryPointApplications},
            } = this.props;

            const rtmpOriginEntryPointApplication = rtmpOriginEntryPointApplications.find((application) => (
                parsedUrl
                && value === application.id
                && parsedUrl.host === application.rtmp_origin_entry_point.host
                && parsedUrl.path === application.rtmp_origin_entry_point_application_type.path
            ));

            this.setField('rtmp_origin_entry_point_application', {
                customWarning: (rtmpOriginEntryPointApplication)
                    ? null
                    : VARIABLES.TV_STREAMING_RTMP_ORIGIN_ENTRY_POINT_APPLICATION_WARNING_EDIT_MODE(streamUrl),
            });
        } else {
            this.setField('rtmp_origin_entry_point_application', {customWarning: null});
        }
    };

    showHideTvStreamingWarningForRtmpOriginStreamName = (value, rtmpOriginStreamNames) => {
        if (
            this.props.formParams.id
            && value
            && this.isTvStreaming()
        ) {
            const streamUrl = this.getTvStreamingEncoderStreamUrl(),
                parsedUrl = parseTvStreamingEncoderStreamUrl(streamUrl);

            const rtmpOriginStreamName = rtmpOriginStreamNames.find((streamName) => (
                parsedUrl
                && streamName.key === value
                && streamName.text === parsedUrl.streamName
            ));

            this.setField('rtmp_origin_stream_name', {
                customWarning: (rtmpOriginStreamName)
                    ? null
                    : VARIABLES.TV_STREAMING_RTMP_ORIGIN_STREAM_NAME_WARNING_EDIT_MODE(streamUrl),
            });
        } else {
            this.setField('rtmp_origin_stream_name', {customWarning: null});
        }
    };

    onChangeCallbackProductionCompany = (value) => {
        if (value) {
            this.setField('contact_person', {defaultValue: null, disabled: true, loading: true});
            this.setField('rtmp_origin_stream_name', {
                defaultValue: null,
                disabled: true,
                loading: true,
            });

            this.queryContactPersonsAndOriginStreamNameOfProductionCompany(value)
                .then((data) => {
                    const contactPersonFieldData = {
                        disabled: false,
                        loading: false,
                        options: data.data.contactPersons,
                    };

                    if (
                        this.isTvStreaming()
                        && !this.props.formParams.id
                        && VARIABLES.TV_STREAMING_PRODUCTION_COMPANY === convertToInt(value)
                    ) {
                        contactPersonFieldData.defaultValue = `${VARIABLES.TV_STREAMING_CONTACT_PERSON}`;

                        const {
                            formParams,
                            GraphQLOptionsData: {rtmpOriginEntryPointApplications},
                        } = this.props;

                        const streamUrl = _get(formParams, 'event.sr_event.match.tv_streaming_encoder.stream_url', false),
                            parsedUrl = parseTvStreamingEncoderStreamUrl(streamUrl);

                        const rtmpOriginEntryPointApplication = rtmpOriginEntryPointApplications.find((application) => (
                            parsedUrl
                            && parsedUrl.host === application.rtmp_origin_entry_point.host
                            && parsedUrl.path === application.rtmp_origin_entry_point_application_type.path
                        ));

                        const rtmpOriginStreamName = data.data.rtmpOriginStreamNames.find((streamName) => (
                            parsedUrl && streamName.text === parsedUrl.streamName
                        ));

                        if (rtmpOriginEntryPointApplication) {
                            this.setField('rtmp_origin_entry_point_application', {
                                defaultValue: rtmpOriginEntryPointApplication.value,
                            });
                        } else {
                            this.setField('rtmp_origin_entry_point_application', {
                                customWarning:
                                    VARIABLES.TV_STREAMING_RTMP_ORIGIN_ENTRY_POINT_APPLICATION_WARNING_ADD_MODE(streamUrl),
                            });
                        }

                        if (rtmpOriginStreamName) {
                            this.setField('rtmp_origin_stream_name', {
                                defaultValue: rtmpOriginStreamName.value,
                            });
                        } else {
                            this.setField('rtmp_origin_stream_name', {
                                customWarning: VARIABLES.TV_STREAMING_RTMP_ORIGIN_STREAM_NAME_WARNING_ADD_MODE(streamUrl),
                            });
                        }
                    }

                    this.setField('contact_person', contactPersonFieldData);
                    this.setField('rtmp_origin_stream_name', {
                        disabled: false,
                        loading: false,
                        options: data.data.rtmpOriginStreamNames,
                    });
                    this.setState(() => ({
                        contactPersonsAndOriginStreamNameOfProductionCompany: data.data,
                    }));
                })
                .finally(() => {
                    this.setState(() => ({
                        queryContactPersonsAndOriginStreamNameOfProductionCompanyLoading: false,
                    }));
                });
        }
    };

    onChangeCallbackMediaConnectOriginEntryPoint = (value) => {
        if (value) {
            this.queryMediaConnectOriginEntryPointsFeed(value)
                .then((data) => {
                    if (2 === data.data.mediaconnectOriginEntryPoint.mediaconnect_origin_feeds.length) {
                        this.setField('mediaconnect_origin_feeds', {hidden: false, defaultValue: '2,1'});
                    } else {
                        this.setField('mediaconnect_origin_feeds', {hidden: true, defaultValue: '1'});
                    }
                });
        } else {
            this.setField('mediaconnect_origin_feeds', {
                hidden: true,
                value: null,
                defaultValue: null,
            });
        }
    };

    prepareFormFieldsForEditing(eventContent, eventContentTypes, formParams, formValues, prevFormValues) {
        this.setState(() => ({
            currentSelectedEventContentType: eventContent.event_content_type.id,
        }));

        /* General Info Form */
        this.setGeneralTabForm(eventContent, eventContentTypes, formValues, prevFormValues);

        /* Input Form */
        this.setInputTabForm(formValues);

        /* Distribution Form */
        this.setDistributionTabForm(formParams);
    }

    setDistributionTabForm(formParams) {
        this.setAvailableProducts(formParams, true);
        this.setField('event_content_type', {disabled: true});
    }

    changeAvailableProducts(formParams, isProcessingDistributions) {
        const rightScopesOfPropertyLicence = this.state.RightScopesOfPropertyLicence;

        if (rightScopesOfPropertyLicence) {
            let availableProducts = [];

            const filteredRightsScopes = rightScopesOfPropertyLicence.data?.rightsScopesAndProducts?.rights_scopes?.map(
                scope => getRightScopesByEventContentVariant(scope, this.state.selectedEventContentVariant)
            );
            this.setState(() => ({
                rightsScopes: rightScopesOfPropertyLicence.data?.rightsScopesAndProducts?.rights_scopes,
                filteredRightsScopes: filteredRightsScopes,
                propertyLicenceTechSetups: rightScopesOfPropertyLicence.data?.propertyLicenceTechSetups,
            }), () => {
                availableProducts = rightScopesOfPropertyLicence.data?.rightsScopesAndProducts?.products;
                this.showAvailableProducts(availableProducts);

                if (isProcessingDistributions) {
                    this.selectProductsBasedOnEventContentDistributionData(
                        this.state.defaultForm_data.event_content.distributions
                    );
                } else {
                    if (0 < rightScopesOfPropertyLicence.data.propertyLicenceTechSetups.length) {
                        this.selectProductsBasedOnPropertyLicenceTechSetup(
                            this.state.propertyLicenceTechSetups
                        );
                    }
                }

                this.setState(() => ({
                    availableProducts: availableProducts,
                    processingDistributions: false,
                }));
            });
        }
    }

    setAvailableProducts(formParams, isProcessingDistributions) {
        if (this.state.availableProducts !== undefined || this.state.queryRightScopesOfPropertyLicenceLoaded) {
            return;
        }

        if (isProcessingDistributions) {
            this.setState(() => ({processingDistributions: true}));
        }

        this
            .queryRightScopesOfPropertyLicence(formParams.event.property_licence.id, formParams.event.id)
            .then((result) => {
                const contentVariantOptions = getEventContentVariantDropdownOptions(result.data?.rightsScopesAndProducts?.rights_scopes),
                    selectedContentVariant = getInitialEventContentVariant(formParams, contentVariantOptions);

                this.setField('event_content_variant', {
                    options: contentVariantOptions,
                    defaultValue: selectedContentVariant,
                });

                this.setState(() => ({
                    RightScopesOfPropertyLicence: result,
                    selectedEventContentVariant: selectedContentVariant,
                    queryRightScopesOfPropertyLicenceLoaded: true,
                }), () => {
                    this.changeAvailableProducts(formParams, isProcessingDistributions);
                });
            });
    }

    setMediaConnectOriginFeeds = (mediaconnectOriginEntryPointId) => {
        if (mediaconnectOriginEntryPointId) {
            this.queryMediaConnectOriginEntryPointsFeed(mediaconnectOriginEntryPointId)
                .then((data) => {
                    if (2 === data.data.mediaconnectOriginEntryPoint.mediaconnect_origin_feeds.length) {
                        this.setField('mediaconnect_origin_feeds', {hidden: false});
                    }
                });
        }
    };

    setInputTabForm(formValues) {
        const originStreamType = convertToInt(formValues.origin_stream_type);
        const encodingDatacenterId = convertToInt(formValues.encoding_datacenter);

        if (formValues.has_input) {
            this.setField(
                [
                    'contribution_type',
                    'encoding_datacenter',
                    'encoding_redundancy_type',
                    'production_company',
                    'contact_person',
                    'origin_stream_type',
                    'sequence',
                ],
                {hidden: false}
            );

            this.showFieldsBasedOnOriginStreamType(originStreamType);
            this.setField('has_input', {checked: true});

            if (VARIABLES.ORIGIN_STREAM_TYPE_MEDIACONNECT === originStreamType
                && encodingDatacenterId
                && this.state.mediaConnectOriginEntryPointsEncodingDatacenterId !== encodingDatacenterId
                && !this.state.queryMediaConnectOriginEntryPointsOfEncodingDatacenterLoading
            ) {
                this.setMediaConnectOfEncodingDataCenterInput(
                    formValues.encoding_datacenter,
                    formValues.mediaconnect_origin_entry_point
                );
            }

            if (convertToInt(originStreamType) === VARIABLES.ORIGIN_STREAM_TYPE_SRT) {
                showSrtFieldsBasedOnFormValues(this.props.formValues, this.setField);
            }
        }

        if (
            !this.state.udpAndSdiOriginEntryPointsOfEncodingDatacenter
            && (
                VARIABLES.ORIGIN_STREAM_TYPE_UDP === originStreamType
                || VARIABLES.ORIGIN_STREAM_TYPE_SDI === originStreamType
            )
            && (
                this.state.queryUdpAndSdiOriginEntryPointsOfEncodingDatacenterLoading === undefined
                || !this.state.queryUdpAndSdiOriginEntryPointsOfEncodingDatacenterLoading
            )
        ) {
            this.setField(['udp_origin_entry_point', 'sdi_origin_entry_point'], {disabled: true, loading: true});

            this
                .queryUdpAndSdiOriginEntryPointsOfEncodingDatacenter(
                    formValues.encoding_datacenter
                )
                .then((data) => {
                    this.setField('udp_origin_entry_point', {
                        defaultValue: formValues.udp_origin_entry_point,
                        disabled: false,
                        loading: false,
                        options: parseOriginEntryPointsOptions(
                            data.data.udpOriginEntryPoints,
                            this.getTournamentOfEvent(),
                            this.getContentCategoryOfEvent()
                        ),
                    });

                    this.setField('sdi_origin_entry_point', {
                        defaultValue: formValues.sdi_origin_entry_point,
                        disabled: false,
                        loading: false,
                        options: data.data.sdiOriginEntryPoints,
                    });

                    this.setState(() => ({
                        udpAndSdiOriginEntryPointsOfEncodingDatacenter: data.data,
                    }));
                })
                .finally(() => {
                    this.setState(() => ({queryUdpAndSdiOriginEntryPointsOfEncodingDatacenterLoading: false}));
                });
        }

        if (
            !this.state.contactPersonsAndOriginStreamNameOfProductionCompany
            && !this.state.queryContactPersonsAndOriginStreamNameOfProductionCompanyLoading
            && formValues.production_company
        ) {
            this.setField(['contact_person', 'rtmp_origin_stream_name'], {disabled: true, loading: true});
            this
                .queryContactPersonsAndOriginStreamNameOfProductionCompany(formValues.production_company)
                .then((data) => {
                    this.setField('contact_person', {
                        defaultValue: formValues.contact_person || null,
                        disabled: false,
                        loading: false,
                        options: data.data.contactPersons,
                    });

                    this.setField('rtmp_origin_stream_name', {
                        defaultValue: formValues.rtmp_origin_stream_name,
                        disabled: false,
                        loading: false,
                        options: data.data.rtmpOriginStreamNames,
                    });

                    this.setState(() => ({
                        contactPersonsAndOriginStreamNameOfProductionCompany: data.data,
                    }));
                })
                .finally(() => {
                    this.setState(() => ({
                        queryContactPersonsAndOriginStreamNameOfProductionCompanyLoading: false,
                    }));
                });
        }

        this.showHideTvStreamingWarningForRtmpOriginEntryPointApplication(
            formValues.rtmp_origin_entry_point_application
        );

        if (this.state.contactPersonsAndOriginStreamNameOfProductionCompany) {
            this.showHideTvStreamingWarningForRtmpOriginStreamName(
                formValues.rtmp_origin_stream_name,
                this.state.contactPersonsAndOriginStreamNameOfProductionCompany.rtmpOriginStreamNames
            );
        }
    }

    setGeneralTabForm(eventContent, eventContentTypes, formValues, prevFormValues) {
        const {
            force_broadcast,
            force_broadcast_margin,
            force_delivery,
            force_delivery_duration,
            start_broadcast_automatically,
            set_as_delivered_automatically,
        } = formValues;

        this.showFieldsByEventContentType(
            this.selectedEventContentDistribution(eventContent.event_content_type.id, eventContentTypes)
        );

        const hasLiveDistribution = _find(eventContent.event_content_type.distribution_types,
            {id: VARIABLES.DISTRIBUTION_TYPE_LIVE + ''});

        if (hasLiveDistribution) {
            this.setField('start_broadcast_automatically', {
                hidden: false,
                disabled: false,
            });
            this.setField('set_as_delivered_automatically', {
                hidden: false,
                disabled: false,
            });
        }

        this.setField('is_247_stream', {hidden: !hasLiveDistribution, disabled: !hasLiveDistribution});

        if (!this.state.event_content_start_datetime
            && !this.state.event_content_end_datetime
        ) {
            const startDateTime = formValues.start_datetime || eventContent.start_datetime;
            const endDateTime = formValues.end_datetime || eventContent.end_datetime;

            this.setState(() => ({
                event_content_start_datetime: startDateTime,
                event_content_end_datetime: endDateTime,
            }));

            this.setField('start_datetime', {
                defaultValue: startDateTime,
                disabled: formValues.synchronise_times,
                timestamp: Date.now(),
            });
            this.setField('end_datetime', {
                defaultValue: endDateTime,
                disabled: formValues.synchronise_times,
                timestamp: Date.now(),
            });
        }

        if (start_broadcast_automatically && _isNil(force_broadcast)) {
            this.setField('force_broadcast', {
                hidden: false,
                defaultValue: !_isNil(force_broadcast_margin),
            });
        }

        if (set_as_delivered_automatically && _isNil(force_delivery)) {
            this.setField('force_delivery', {
                hidden: !set_as_delivered_automatically,
                defaultValue: !_isNil(force_delivery_duration),
            });
        }

        if (force_broadcast && force_broadcast !== prevFormValues.force_broadcast) {
            this.setField('force_broadcast_margin', {hidden: false});
        }

        if (force_delivery && force_delivery !== prevFormValues.force_delivery) {
            this.setField('force_delivery_duration', {hidden: false});
        }
    }

    prepareForm(formParams, formValues, loading) {
        if (formParams.id || loading) {
            return;
        }

        if (
            formParams
            && !this.state.event_content_start_datetime
            && !this.state.event_content_end_datetime
        ) {
            this.setState(() => ({
                event_content_start_datetime: formParams.event.start_datetime,
                event_content_end_datetime: formParams.event.end_datetime,
            }));

            this.setField('start_datetime', {
                defaultValue: formParams.event.start_datetime,
                disabled: formValues.synchronise_times,
            });
            this.setField('end_datetime', {
                defaultValue: formParams.event.end_datetime,
                disabled: formValues.synchronise_times,
            });
        }

        if (!formValues.production_company) {
            this.setField(['contact_person', 'rtmp_origin_stream_name'], {disabled: true, loading: false});
        }

        if (!formValues.encoding_datacenter) {
            this.setField(['udp_origin_entry_point', 'sdi_origin_entry_point'], {disabled: true, loading: false});
        }

        this.setAvailableProducts(formParams, false);

        if (formValues.has_input && convertToInt(formValues.origin_stream_type) === VARIABLES.ORIGIN_STREAM_TYPE_SRT) {
            showSrtFieldsBasedOnFormValues(formValues, this.setField);
        }
    }

    prepareDataForSubmit = (data) => {
        this.setState(() => ({submitData: data}));

        return this.prepareData(data);
    };

    getGenerateTargetsCheckboxValue = () => (
        !this.state.defaultForm_fields.generateTargets.props.hidden
            ? this.state.submitData.generateTargets
            : false
    );

    prepareData = (data) => {
        let event_content = {
                ...this.props.Model.dataMap[this.props.Model.entityDataMapKey],
                ...data,
            },
            id = event_content.id,
            dataToSave = {
                ...this.prepareGeneralInformationData(data),
                ...this.prepareInputData(data),
            };

        if (id) {
            dataToSave = Object.assign(
                dataToSave,
                {id: convertToInt(id)}
            );
        } else {
            dataToSave.event = convertToInt(this.props.formParams.event.id);
        }

        return dataToSave;
    };

    getEndDatetime = (data) => {
        return data.end_datetime ? data.end_datetime + ':00' : null;
    };

    prepareGeneralInformationData = (data) => {
        let startBroadcastAutomatically = data.start_broadcast_automatically || false;
        let setAsDeliveredAutomatically = data.set_as_delivered_automatically || false;

        if (this.state.defaultForm_fields.start_broadcast_automatically.props.disabled) {
            startBroadcastAutomatically = false;
        }

        if (this.state.defaultForm_fields.set_as_delivered_automatically.props.disabled) {
            setAsDeliveredAutomatically = false;
        }

        const dataToSave = {
            event_content_type: convertToInt(data.event_content_type),
            event_content_variant: convertToInt(data.event_content_variant),
            force_broadcast_margin: (data.start_broadcast_automatically && data.force_broadcast)
                ? convertToInt(data.force_broadcast_margin)
                : null,
            force_delivery_duration: (data.set_as_delivered_automatically && data.force_delivery)
                ? convertToInt(data.force_delivery_duration)
                : null,
            is_247_stream: data.is_247_stream || false,
            is_main_content: data.is_main_content || false,
            name: data.name || null,
            start_broadcast_automatically: startBroadcastAutomatically,
            set_as_delivered_automatically: setAsDeliveredAutomatically,
        };

        if (this.props.formParams.id) {
            dataToSave.hide_in_live_control = data.hide_in_live_control || false;
        }

        const endDatetime = this.getEndDatetime(data);

        if (this.selectedEventContentDistribution(data.event_content_type, this.state.eventContentTypes) === HAS_NO_VOD) {
            dataToSave.video_length = null;
            dataToSave.video_duration = null;
            dataToSave.synchronise_times = data.synchronise_times;
            dataToSave.start_datetime = data.start_datetime + ':00';
            dataToSave.end_datetime = endDatetime;
        } else {
            dataToSave.video_length = data.video_length ? convertToInt(data.video_length) : null;
            dataToSave.video_duration = data.video_duration ? convertToInt(data.video_duration) : null;
            dataToSave.synchronise_times = data.synchronise_times;
            dataToSave.start_datetime = data.start_datetime ? data.start_datetime + ':00' : null;
            dataToSave.end_datetime = endDatetime;
        }

        return dataToSave;
    };

    prepareInputData = (data) => {
        const isFreeTextSelected = data.srt_origin_entry_point === VARIABLES.FREE_TEXT;

        if (!data.has_input) {
            return {
                has_input: false,
                event_content_input: {},
            };
        }

        let dataToSave = {
                has_input: data.has_input,
                event_content_input: {
                    contribution_type: convertToInt(data.contribution_type),
                    encoding_datacenter: convertToInt(data.encoding_datacenter),
                    encoding_redundancy_type: convertToInt(data.encoding_redundancy_type),
                    production_company: convertToInt(data.production_company),
                    contact_person: convertToInt(data.contact_person),
                    origin_stream_type: convertToInt(data.origin_stream_type),
                    sequence: convertToInt(data.sequence),
                },
            }, additionalData = {};

        switch (convertToInt(data.origin_stream_type)) {
            case VARIABLES.ORIGIN_STREAM_TYPE_RTMP:
                additionalData = {
                    event_content_input: {
                        rtmp_origin_entry_point_application: convertToInt(data.rtmp_origin_entry_point_application),
                        rtmp_origin_stream_name: convertToInt(data.rtmp_origin_stream_name) || null,
                        udp_origin_entry_point: null,
                        sdi_origin_entry_point: null,
                        http_origin_entry_point_url: null,
                        mediaconnect_origin_entry_point: null,
                        mediaconnect_origin_feeds: null,
                        srt_origin_entry_point: null,
                        failover_srt_origin_entry_point: null,
                        srt_origin_entry_point_url: null,
                        failover_srt_origin_entry_point_url: null,
                        is_srt_origin_entry_point_in_failover: null,
                        srt_has_scte35: null,
                        srt_ad_splice_config: null,
                    },
                };
                break;
            case VARIABLES.ORIGIN_STREAM_TYPE_UDP:
                additionalData = {
                    event_content_input: {
                        rtmp_origin_entry_point_application: null,
                        rtmp_origin_stream_name: null,
                        udp_origin_entry_point: convertToInt(data.udp_origin_entry_point),
                        sdi_origin_entry_point: null,
                        http_origin_entry_point_url: null,
                        mediaconnect_origin_entry_point: null,
                        mediaconnect_origin_feeds: null,
                        srt_origin_entry_point: null,
                        failover_srt_origin_entry_point: null,
                        srt_origin_entry_point_url: null,
                        failover_srt_origin_entry_point_url: null,
                        is_srt_origin_entry_point_in_failover: null,
                        srt_has_scte35: null,
                        srt_ad_splice_config: null,
                    },
                };
                break;
            case VARIABLES.ORIGIN_STREAM_TYPE_SDI:
                additionalData = {
                    event_content_input: {
                        rtmp_origin_entry_point_application: null,
                        rtmp_origin_stream_name: null,
                        udp_origin_entry_point: null,
                        sdi_origin_entry_point: convertToInt(data.sdi_origin_entry_point),
                        http_origin_entry_point_url: null,
                        mediaconnect_origin_entry_point: null,
                        mediaconnect_origin_feeds: null,
                        srt_origin_entry_point: null,
                        failover_srt_origin_entry_point: null,
                        srt_origin_entry_point_url: null,
                        failover_srt_origin_entry_point_url: null,
                        is_srt_origin_entry_point_in_failover: null,
                        srt_has_scte35: null,
                        srt_ad_splice_config: null,
                    },
                };
                break;
            case VARIABLES.ORIGIN_STREAM_TYPE_HTTP:
                additionalData = {
                    event_content_input: {
                        rtmp_origin_entry_point_application: null,
                        rtmp_origin_stream_name: null,
                        udp_origin_entry_point: null,
                        sdi_origin_entry_point: null,
                        http_origin_entry_point_url: data.http_origin_entry_point_url || null,
                        mediaconnect_origin_entry_point: null,
                        mediaconnect_origin_feeds: null,
                        srt_origin_entry_point: null,
                        failover_srt_origin_entry_point: null,
                        srt_origin_entry_point_url: null,
                        failover_srt_origin_entry_point_url: null,
                        is_srt_origin_entry_point_in_failover: null,
                        srt_has_scte35: null,
                        srt_ad_splice_config: null,
                    },
                };
                break;
            case VARIABLES.ORIGIN_STREAM_TYPE_MEDIACONNECT:
                additionalData = {
                    event_content_input: {
                        rtmp_origin_entry_point_application: null,
                        rtmp_origin_stream_name: null,
                        udp_origin_entry_point: null,
                        sdi_origin_entry_point: null,
                        http_origin_entry_point_url: null,
                        mediaconnect_origin_entry_point: convertToInt(data.mediaconnect_origin_entry_point),
                        mediaconnect_origin_feeds: data.mediaconnect_origin_feeds || null,
                        srt_origin_entry_point: null,
                        failover_srt_origin_entry_point: null,
                        srt_origin_entry_point_url: null,
                        failover_srt_origin_entry_point_url: null,
                        is_srt_origin_entry_point_in_failover: null,
                        srt_has_scte35: null,
                        srt_ad_splice_config: null,
                    },
                };
                break;
            case VARIABLES.ORIGIN_STREAM_TYPE_SRT:

                if (isFreeTextSelected) {
                    if (null === data.srt_has_scte35) {
                        data.srt_has_scte35 = false;
                    }

                    if (data.failover_srt_origin_entry_point_url) {
                        data.is_srt_origin_entry_point_in_failover = !!data.is_srt_origin_entry_point_in_failover;
                    } else {
                        data.is_srt_origin_entry_point_in_failover = null;
                    }
                } else {
                    if (!data.override_ad_setup) {
                        data.srt_has_scte35 = null;
                    } else {
                        if (null === data.srt_has_scte35) {
                            data.srt_has_scte35 = false;
                        }
                    }

                    if (data.failover_srt_origin_entry_point) {
                        data.is_srt_origin_entry_point_in_failover = !!data.is_srt_origin_entry_point_in_failover;
                    } else {
                        data.is_srt_origin_entry_point_in_failover = null;
                    }
                }

                additionalData = {
                    event_content_input: {
                        rtmp_origin_entry_point_application: null,
                        rtmp_origin_stream_name: null,
                        udp_origin_entry_point: null,
                        sdi_origin_entry_point: null,
                        http_origin_entry_point_url: null,
                        mediaconnect_origin_entry_point: null,
                        mediaconnect_origin_feeds: null,
                        srt_origin_entry_point: isFreeTextSelected ? null : convertToInt(data.srt_origin_entry_point) || null,
                        failover_srt_origin_entry_point: isFreeTextSelected ? null : convertToInt(data.failover_srt_origin_entry_point) || null,
                        srt_origin_entry_point_url: isFreeTextSelected ? data.srt_origin_entry_point_url || null : null,
                        failover_srt_origin_entry_point_url: isFreeTextSelected ? data.failover_srt_origin_entry_point_url || null: null,
                        is_srt_origin_entry_point_in_failover: data.is_srt_origin_entry_point_in_failover,
                        srt_has_scte35: data.srt_has_scte35,
                        srt_ad_splice_config: data.srt_has_scte35 ? convertToInt(data.srt_ad_splice_config) : null,
                    },
                };
                break;
        }

        dataToSave.event_content_input = Object.assign(
            dataToSave.event_content_input,
            additionalData.event_content_input
        );

        if (dataToSave.has_input && !data.eventContentInputId) {
            const event = this.props.formParams.event,
                sportsWithMarginNull = [
                    VARIABLES.SPORT_TENNIS,
                    VARIABLES.SPORT_BADMINTON,
                ];

            dataToSave.event_content_input.encoding_start_margin = ENCODING_START_MARGIN;

            let sportId = null;

            if (event.sr_event) {
                if (event.sr_event.match?.tournament) {
                    sportId = convertToInt(event.sr_event.match.tournament.tournament_category.sport.id);
                } else {
                    sportId = convertToInt(event.sr_event.tournament.tournament_category.sport.id);
                }
            }

            if (sportsWithMarginNull.includes(sportId)) {
                dataToSave.event_content_input.encoding_start_margin = null;
            }

            const eventTypeId = this.props.formParams.event.event_type.id;

            dataToSave.event_content_input.encoding_end_margin = null !== data.end_datetime || (EVENT_TYPE_SPORTRADAR === convertToInt(eventTypeId))
                ? ENCODING_END_MARGIN
                : null;
        }

        return dataToSave;
    };

    prepareDistributionData = (data) => {
        let distributionsNestedMap = {};

        this.props.formParams.products.forEach((product) => {
            distributionsNestedMap[`${product.id}_1`] = {
                product: convertToInt(product.id),
                distribution_type: VARIABLES.DISTRIBUTION_TYPE_LIVE,
            };
            distributionsNestedMap[`${product.id}_2`] = {
                product: convertToInt(product.id),
                distribution_type: VARIABLES.DISTRIBUTION_TYPE_RECORDING,
            };
            distributionsNestedMap[`${product.id}_3`] = {
                product: convertToInt(product.id),
                distribution_type: VARIABLES.DISTRIBUTION_TYPE_VOD,
            };
        });

        const distributions = [];

        for (const key of Object.keys(data)) {
            Object.keys(distributionsNestedMap).forEach((nestedKey) => {
                if (key.startsWith(nestedKey)) {
                    distributionsNestedMap[nestedKey][key.replace(`${nestedKey}_`, '')] = data[key];
                }
            });
        }

        Object.keys(distributionsNestedMap).forEach((nestedKey) => {
            // Product id build in this way "productID_distributionTypeId", ex. "10_1", "1_2", etc...
            const productId = nestedKey.split('_').shift();

            if (data[`product_${productId}`]) {
                const preparedDistribution = this.prepareDistributionForSubmit(distributionsNestedMap[nestedKey], data);

                if (null !== preparedDistribution) {
                    distributions.push(preparedDistribution);
                }
            }
        });

        return {
            distributions: distributions.filter((e) => (e)),
        };
    };

    prepareDistributionForSubmit = (distribution, data) => {
        const distributionMap = {
            product: '',
            distribution_type: '',
            product_status: '',
            broadcast_status: null,
            synchronise_times: false,
            start_datetime: null,
            end_datetime: null,
            name: null,
        };

        let isDistributionHidden = this.state[
            `product_${distribution.product}_distribution_type_${distribution.distribution_type}_always_hidden`
        ];

        if (!distribution.distribution_usage || isDistributionHidden) {
            return null;
        }

        delete distribution.distribution_usage;

        const preparedDistribution = Object.assign(distributionMap, distribution);

        preparedDistribution.product_status = convertToInt(preparedDistribution.product_status);

        if (preparedDistribution.synchronise_times) {
            preparedDistribution.start_datetime = data.start_datetime
                ? data.start_datetime + ':00'
                : null;
            preparedDistribution.end_datetime = data.end_datetime
                ? data.end_datetime + ':00'
                : null;
        }

        preparedDistribution.clients = (VARIABLES.CLIENT_SELECTION_TYPE_ALL !== convertToInt(distribution.client_selection_type))
            ? distribution.clients
            : [];

        preparedDistribution.broadcast_status = VARIABLES.DISTRIBUTION_TYPE_RECORDING !== distribution.distribution_type
            ? convertToInt(distribution.broadcast_status)
            : null;

        return preparedDistribution;
    };

    selectedEventContentDistribution = (selectedValue, eventContentTypes) => {
        if (!eventContentTypes) {
            return false;
        }

        const selectedEventContent = _find(eventContentTypes, {key: selectedValue});

        let hasVod = selectedEventContent.distribution_types.some((distribution_type) => (
            VARIABLES.DISTRIBUTION_TYPE_VOD === convertToInt(distribution_type.id)
        ));

        if (hasVod && 1 === selectedEventContent.distribution_types.length) {
            return HAS_VOD_ONLY;
        } else if (hasVod) {
            return HAS_VOD;
        }

        return HAS_NO_VOD;
    };

    hasAlreadyMainContent = (eventContents) => (eventContents.some((item) => (true === item.is_main_content)));

    toggleGenerateTargets = (fields) => {
        const hasNewDistributionSelected = this.hasNewDistributionSelected(
                fields,
                _get(this.props, 'GraphQLEntityData.event_content.distributions', []),
                this.props.formParams.products
            ),
            hideGenerateTargets = !(hasNewDistributionSelected && !!this.state.propertyLicenceTechSetups.length),
            hideGenerateTargetsWarning = !(hasNewDistributionSelected && !this.state.propertyLicenceTechSetups.length);

        this.setField('generateTargets', {
            hidden: hideGenerateTargets,
        });
        this.setField('generateTargetsWarning', {
            hidden: hideGenerateTargetsWarning,
        });
    };

    hasNewDistributionSelected = (fields, existingDistributions, products) => (
        0 < products.filter((product) => (
            0 < DISTRIBUTION_TYPE_IDS.filter((distribution) => {
                const productStateKey = _findKey(fields, (field) => (
                        `product_${product.id}` === _get(field, 'props.name', '')
                    )),
                    distributionTypeStateKey = _findKey(fields, (field) => (
                        `${product.id}_${distribution}_distribution_usage` === _get(field, 'props.name', '')
                    ));

                return (
                    _get(fields[productStateKey], 'props.defaultValue', false)
                    && _get(fields[distributionTypeStateKey], 'props.defaultValue', false)
                    && !existingDistributions.some((existingDistribution) => (
                        existingDistribution.product.id === product.id
                        && convertToInt(existingDistribution.distribution_type.id) === distribution
                    ))
                );
            }).length
        )).length
    );

    onChangeProductSwitch = (product, toggle) => {
        if (this.checkIfDistributionShouldBeShown(product, VARIABLES.DISTRIBUTION_TYPE_LIVE)) {
            this.toggleDistributionGroup(product, VARIABLES.DISTRIBUTION_TYPE_LIVE, toggle);
        }

        if (this.checkIfDistributionShouldBeShown(product, VARIABLES.DISTRIBUTION_TYPE_RECORDING)) {
            this.toggleDistributionGroup(product, VARIABLES.DISTRIBUTION_TYPE_RECORDING, toggle);
        }

        if (this.checkIfDistributionShouldBeShown(product, VARIABLES.DISTRIBUTION_TYPE_VOD)) {
            this.toggleDistributionGroup(product, VARIABLES.DISTRIBUTION_TYPE_VOD, toggle);
        }

        this.setFieldOnDistributionChange(`product_${product}`, toggle);
    };

    onChangeDistributionUsageCheckbox = (product, distributionType, toggle) => {
        this.setField(`${product}_${distributionType}_synchronise_times`, {hidden: !!toggle});
        this.setField(`${product}_${distributionType}_start_datetime`, {hidden: !!toggle});
        this.setField(`${product}_${distributionType}_end_datetime`, {hidden: !!toggle});
        this.setField(`${product}_${distributionType}_product_status`, {hidden: !!toggle});
        this.setField(`${product}_${distributionType}_name`, {hidden: !!toggle});
        this.setField(`${product}_${distributionType}_client_selection_type`, {hidden: !!toggle});
        this.setField(`${product}_${distributionType}_clients`, {
            hidden: (
                !!toggle
                || VARIABLES.CLIENT_SELECTION_TYPE_ALL === convertToInt(
                    this.props.formValues[`${product}_${distributionType}_client_selection_type`]
                )
            ),
        });

        if (VARIABLES.DISTRIBUTION_TYPE_RECORDING !== distributionType) {
            this.setField(`${product}_${distributionType}_broadcast_status`, {hidden: !!toggle});
        }

        this.setFieldOnDistributionChange(`${product}_${distributionType}_distribution_usage`, !toggle);
    };

    setFieldOnDistributionChange = (id, toggle) => {
        this.setField(
            id,
            {checked: toggle, defaultValue: toggle},
            this.toggleGenerateTargets
        );
    };

    onChangeEventContentType = (value) => {
        if (!value) {
            return;
        }

        let contentTypes = _find(this.state.eventContentTypes, {value: value}),
            hasLiveDistribution = _find(contentTypes.distribution_types, {id: VARIABLES.DISTRIBUTION_TYPE_LIVE + ''}),
            isStartBroadcastAutomaticallyDisabled = false,
            isSetAsDeliveredAutomaticallyDisabled = false;

        if (!hasLiveDistribution) {
            isStartBroadcastAutomaticallyDisabled = true;
            isSetAsDeliveredAutomaticallyDisabled = true;
        }

        this.setField('start_broadcast_automatically', {
            hidden: isStartBroadcastAutomaticallyDisabled,
            disabled: isStartBroadcastAutomaticallyDisabled,
        });

        this.setField('set_as_delivered_automatically', {
            hidden: isSetAsDeliveredAutomaticallyDisabled,
            disabled: isSetAsDeliveredAutomaticallyDisabled,
        });

        this.setField('is_247_stream', {
            checked: false,
            defaultValue: false,
            disabled: !hasLiveDistribution,
            hidden: !hasLiveDistribution,
        });

        this.showFieldsByEventContentType(
            this.selectedEventContentDistribution(value, this.state.eventContentTypes)
        );

        this.setInputAndDistributionFields(
            this.state.propertyLicenceTechSetupProducts,
            value
        );

        this.setState(() => ({
            currentSelectedEventContentType: value,
        }));
    };

    showFieldsByEventContentType = (type) => {
        if (type === HAS_NO_VOD) {
            this.setField(['video_length', 'video_duration'], {hidden: true});
        } else {
            this.setField(['video_length', 'video_duration'], {hidden: false});
        }
    };

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);


        if (this.props.submitting) {
            return;
        }

        const {
            formParams: {products},
            formValues: {srt_ad_splice_config},
            GraphQLOptionsData: {
                adSpliceConfigs,
                clientsForProducts,
                rtmpOriginEntryPointApplications,
                eventContentTypes,
                loading,
            },
        } = this.props;


        if (this.state.defaultForm_data?.event_content && !loading && prevProps.formValues !== this.props.formValues) {
            this.prepareFormFieldsForEditing(
                this.props.GraphQLEntityData.event_content,
                eventContentTypes,
                this.props.formParams,
                this.props.formValues,
                prevProps.formValues
            );

        } else if (this.props.formValues !== prevProps.formValues) {
            this.prepareForm(this.props.formParams, this.props.formValues, loading);
        }

        if (this.state.eventContentTypes !== eventContentTypes) {
            this.setState({eventContentTypes});
        }


        if (!this.getIsLoading()) {
            if (this.props.GraphQLOptionsData !== prevProps.GraphQLOptionsData ||
                this.props.GraphQLEntityData !== prevProps.GraphQLEntityData) {
                this.updateAdSpliceConfigs(adSpliceConfigs, srt_ad_splice_config);
            }
        }

        if (!loading && this.props.GraphQLOptionsData !== prevProps.GraphQLOptionsData) {
            this.updateClientsForProducts(products, clientsForProducts);
            prepareSrtOriginEntryPointsOptions(
                this.querySrtOriginEntryPointsOptions,
                this.setField,
                this.getTournamentOfEvent,
                this.getContentCategoryOfEvent
            );
        }

        if (rtmpOriginEntryPointApplications && rtmpOriginEntryPointApplications
            !== prevProps.GraphQLOptionsData.rtmpOriginEntryPointApplications) {
            this.setField('rtmp_origin_entry_point_application', {
                defaultValue: this.props.formValues.rtmp_origin_entry_point_application,
                options: filterActiveRtmp(
                    rtmpOriginEntryPointApplications, this.props.formValues.rtmp_origin_entry_point_application
                ),
            });
        }

        if (!this.getIsFormLoading() && !this.props.formParams.id && !this.props.formValues.event_content_type) {
            this.setDefaultEventContentType();
        }

        if (prevProps.formValues.failover_srt_origin_entry_point_url && !this.props.formValues.failover_srt_origin_entry_point_url) {
            this.setField('is_srt_origin_entry_point_in_failover', {hidden: true});
        }

        if (prevProps.formValues.srt_origin_entry_point !== this.props.formValues.srt_origin_entry_point) {
            if (this.props.formValues.srt_origin_entry_point === VARIABLES.FREE_TEXT
                && this.props.formValues.failover_srt_origin_entry_point_url) {
                this.setField('is_srt_origin_entry_point_in_failover', {hidden: false});
            }

            if (prevProps.formValues.srt_origin_entry_point
                && this.props.formValues.srt_has_scte35
                && (this.props.formValues.override_ad_setup
                    || this.props.formValues.srt_origin_entry_point === VARIABLES.FREE_TEXT)) {
                this.setField('srt_ad_splice_config', {hidden: false, required: true});
            }
        }

        if (prevProps.formValues.srt_has_scte35 !== this.props.formValues.srt_has_scte35
            && !this.props.formValues.srt_has_scte35) {
            this.setField('srt_ad_splice_config', {hidden: true, required: false});
        }
    }

    updateClientsForProducts(products, clientsForProducts) {
        const hasValidProduct = (clientForProducts, product, distributionType) => (
            clientForProducts.products.some((clientProduct) => (
                clientProduct.product_id === product.id
                && (
                    (VARIABLES.DISTRIBUTION_TYPE_LIVE === distributionType && clientProduct.valid_for_live)
                    || (VARIABLES.DISTRIBUTION_TYPE_RECORDING === distributionType)
                    || (VARIABLES.DISTRIBUTION_TYPE_VOD === distributionType && clientProduct.valid_for_vod)
                )
            ))
        );

        products.forEach((product) => {
            DISTRIBUTION_TYPE_IDS.forEach((distributionType) => {
                this.setField(`${product.id}_${distributionType}_clients`, {
                    options: clientsForProducts?.filter((clientForProducts) => (
                        hasValidProduct(clientForProducts, product, distributionType)
                    )),
                });
            });
        });
    }

    updateAdSpliceConfigs(adSpliceConfigs, srt_ad_splice_config) {
        if (!_isEmpty(adSpliceConfigs)) {
            this.setField('srt_ad_splice_config', {
                options: sortDropdownOptionsAlphanumerically(adSpliceConfigs.map((adSpliceConfig) => ({
                    key: convertToInt(adSpliceConfig.id),
                    text: adSpliceConfig.name,
                    value: convertToInt(adSpliceConfig.id),
                }))),
                defaultValue: convertToInt(srt_ad_splice_config) || null,
            });
        } else {
            this.setField('srt_ad_splice_config', {options: []});
        }
    }

    setDefaultEventContentType() {
        let eventContentTypes = this.eventContentTypeParser(this.props.GraphQLOptionsData.eventContentTypes),
            hasFullmatch = this.hasFullmatch(eventContentTypes),
            fullmatchEventContentExists = this.fullmatchEventContentExists(this.props.formParams.eventContents);

        if (hasFullmatch && !fullmatchEventContentExists) {
            this.setField('event_content_type', {
                defaultValue: VARIABLES.EVENT_CONTENT_TYPE_FULLMATCH.toString(),
            });

            this.setFieldFocus('name');

            this.onChangeEventContentType(VARIABLES.EVENT_CONTENT_TYPE_FULLMATCH.toString());
        }
    }

    hasFullmatch = (eventContentTypes) => (
        eventContentTypes.map((eventContentType) => (
            convertToInt(eventContentType.value)
        )).includes(VARIABLES.EVENT_CONTENT_TYPE_FULLMATCH)
    );

    fullmatchEventContentExists = (eventContents) => (
        0 < eventContents.filter((eventContent) => (
            VARIABLES.EVENT_CONTENT_TYPE_FULLMATCH === convertToInt(eventContent.event_content_type.id)
        )).length
    );

    /**
     * We need to pass ACTUAL value of fields:
     * 1. Logic react on 2 fields: encoding_datacenter and origin_stream_type
     *   - if we change encoding_datacenter,
     *     we have to pass "data.value" as 1st param / this.props.formValues.origin_stream_type as 2nd param
     *   - if we change origin_stream_type,
     *     we have to pass this.props.formValues.encoding_datacenter as 1st param / data.value as 2nd param
     * !!! On change has PREVIOUS VALUE in the this.props.formValues !!!
     * !!! Current value for CHANGED field is in the data.value !!!
     * @param encodingDatacenterIdParam
     * @param originStreamTypeIdParam
     * @param mediaConnectOriginEntryPointId
     */
    onChangeCallbackOriginStreamType = (encodingDatacenterIdParam, originStreamTypeIdParam, mediaConnectOriginEntryPointId) => {
        const encodingDatacenterId = convertToInt(encodingDatacenterIdParam),
            originStreamTypeId = convertToInt(originStreamTypeIdParam);

        if (VARIABLES.ORIGIN_STREAM_TYPE_MEDIACONNECT === originStreamTypeId) {
            this.setMediaConnectOfEncodingDataCenterInput(encodingDatacenterId, mediaConnectOriginEntryPointId);
        } else if (VARIABLES.ORIGIN_STREAM_TYPE_UDP === originStreamTypeId
            || VARIABLES.ORIGIN_STREAM_TYPE_SDI === originStreamTypeId
        ) {
            this.queryUdpAndSdiOriginEntryPointsOfEncodingDatacenter(encodingDatacenterId)
                .then((data) => {
                    this.setField('udp_origin_entry_point', {
                        disabled: false,
                        loading: false,
                        options: parseOriginEntryPointsOptions(
                            data.data.udpOriginEntryPoints,
                            this.getTournamentOfEvent(),
                            this.getContentCategoryOfEvent()
                        ),
                    });
                    this.setField('sdi_origin_entry_point', {
                        disabled: false,
                        loading: false,
                        options: data.data.sdiOriginEntryPoints,
                    });
                })
                .finally(() => {
                    this.setState(() => ({
                        queryUdpAndSdiOriginEntryPointsOfEncodingDatacenterLoading: false,
                    }));
                });
        } else if (VARIABLES.ORIGIN_STREAM_TYPE_SRT === originStreamTypeId) {
            showSrtFieldsBasedOnFormValues(this.props.formValues, this.setField);
        }
    };

    setStateHandler = (stateObject) => {
        this.setState(() => (stateObject));
    };

    processingDistributionsFinished = () => {
        this.setState(() => ({processingDistributions: false}));
        this.props.Modal.setModal({isVisible: false});
        refetchQueryByName('GetEventContents', true);
    };

    showAutomaticTargetGenerationMessage = (data) => {
        const automaticTargetGenerationResponse = data.find((item) => (
            VARIABLES.TYPE_AUTOMATIC_TARGET_GENERATION === item.type
        ));

        if (automaticTargetGenerationResponse) {
            const message = (
                    <>
                        {automaticTargetGenerationResponse.message.split('\\n').map((line, index) => (
                            <div key={`automaticTargetGenerationResponse_line_${index}`}>{line}</div>
                        ))}
                    </>
                ),
                success = responseStatus.HTTP_OK === automaticTargetGenerationResponse.status;

            this.props.MessageBox.addMessage(
                'automaticTargetGeneration',
                message,
                null,
                success ? 'success' : 'warning',
                !success
            );
        }
    };

    eventContentTypeParser = (data) => {
        let event = this.props.formParams.event,
            filteredData = [];

        if (!event) {
            return data;
        }

        if (event.sr_event) {
            let searchId;

            if (event.sr_event.match?.tournament) {
                searchId = event.sr_event.match.tournament.tournament_category.sport.id;
            } else if (event.sr_event.tournament) {
                searchId = event.sr_event.tournament.tournament_category.sport.id;
            }

            filteredData = _filter(data, (item) => {
                if (0 === _filter(item.event_types, (event_type) => {
                    return VARIABLES.EVENT_SPORTRADAR_TYPE === convertToInt(event_type.id);
                }).length) {
                    return false;
                }

                if (0 === item.sports.length) {
                    return true;
                }

                return _find(item.sports, (sport) => {
                    return sport.id === searchId;
                });
            });
        } else if (event.av_event) {
            let searchId = event.av_event.content_category.parent.parent.id;

            filteredData = _filter(data, (item) => {
                if (0 === _filter(item.event_types, (event_type) => {
                    return VARIABLES.EVENT_NON_SPORTRADAR_TYPE === convertToInt(event_type.id);
                }).length) {
                    return false;
                }

                if (0 === item.content_categories.length) {
                    return true;
                }

                return _find(item.content_categories, (contentCategory) => {
                    return contentCategory.id === searchId;
                });
            });
        } else {
            filteredData = data;
        }

        return filteredData;
    };

    /**
     * @ignore
     */
    getFieldCallback = (field) => {
        const callbackForField = this.state.defaultForm_fieldCallbacks[field.props.name],
            props = {};

        if (!callbackForField) {
            return props;
        }

        if ((Form.SemanticInput === field.props.component)
            && ('checkbox' === field.props.type || 'toggle' === field.props.type)) {
            props.onCheckboxChange = this.onChangeCallback;
        }

        if ((Form.SemanticInput == field.props.component) && (!field.props.type || 'input' === field.props.type)) {
            props.onChange = this.onChangeCallback;
        }

        if ((Form.SemanticSelect === field.props.component) || ('select' === field.props.type)) {
            props.onChangeSelect = this.onChangeCallback;
        }

        if ((Form.SemanticInput == field.props.component) && ('date' === field.props.type)) {
            props.onChangeDate = this.onChangeCallback;
        }

        /** EventsConfigurationDistributionForm specific code */
        if (this.props.formParams.products.map((product) => (`product_${product.id}`)).includes(field.props.name)) {
            props.onCheckboxChange = this.onChangeCallback;
        }

        return props;
    };

    redirectToEventContentEditPage = () => {
        window.location.href = getLink('events.configuration.content.edit', {
            id: _get(this.props, 'formParams.event.id', null),
            contentId: _get(this.props, 'formParams.id', null),
        });
    };

    renderDeleteButton = () => {
        return this.state.canDelete
            ? super.renderDeleteButton()
            : super.renderDeleteButton({
                onClick: () => setErrorModal(
                    renderModalErrorObject(CANT_DELETE_CONTENT, this.redirectToEventContentEditPage)
                ),
            });
    };
}

export default connect(null, mapModulesToProps(['MessageBox']))(props => (
    <ApolloConsumer>
        {client => <EventsContentForm {...props} client={client} />}
    </ApolloConsumer>
));
